import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useAPI from '../../hooks/useAPI';
import endpoints from '../../Services/endpointUser';

function Home({ user }) {
  const navigate = useNavigate();
  const isGuest = Boolean(user?.isGuest);
  const donaturId = user?.id || user?.donatur?.id;
  const endpoint = isGuest
    ? null
    : donaturId
      ? endpoints.donatur.getById(donaturId)
      : endpoints.donatur.getAll;
  const { data, loading, error } = useAPI(endpoint);

  // Carousel state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Donation target state
  const [monthlyTarget, setMonthlyTarget] = useState(() => {
    const saved = localStorage.getItem('donationMonthlyTarget');
    return saved ? parseInt(saved) : 0;
  });
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [targetInput, setTargetInput] = useState(monthlyTarget.toString());

  // Berita detail modal state
  const [selectedBerita, setSelectedBerita] = useState(null);
  const [showBeritaModal, setShowBeritaModal] = useState(false);

  // Save monthly target to localStorage
  const handleSaveTarget = () => {
    const target = parseInt(targetInput) || 0;
    if (target > 0) {
      setMonthlyTarget(target);
      localStorage.setItem('donationMonthlyTarget', target.toString());
      setShowTargetModal(false);
    }
  };

  const heroImages = [
    {
      src: 'https://images.unsplash.com/photo-1584551246679-0daf3d275d0f?w=800&h=400&fit=crop',
      alt: 'Masjid dan Ibadah',
      title: 'Tunaikan Zakat dengan Ikhlas',
      subtitle: 'Satu dashboard terpadu untuk mengelola seluruh niat kebaikan dan donasi Anda'
    },
    {
      src: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=800&h=400&fit=crop',
      alt: 'Berbagi dan Kepedulian',
      title: 'Dampak Zakat untuk Mustahik',
      subtitle: 'Lihat bagaimana penyaluran zakat Anda memberikan kebahagiaan bagi sesama'
    },
    {
      src: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&h=400&fit=crop',
      alt: 'Pendidikan dan Pengembangan',
      title: 'Zakat untuk Masa Depan',
      subtitle: 'Investasi sosial jangka panjang dalam pendidikan dan kesejahteraan umat'
    }
  ];

  // Auto-slide effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % heroImages.length
      );
    }, 4000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, [heroImages.length]);

  let currentDonatur = null;

  if (data?.donatur) {
    if (Array.isArray(data.donatur)) {
      currentDonatur =
        data.donatur.find((item) => item.id === donaturId) ||
        data.donatur[0] ||
        null;
    } else {
      currentDonatur = data.donatur;
    }
  } else if (Array.isArray(data) && user) {
    currentDonatur =
      data.find((item) => {
        if (user.email && item.email) return item.email === user.email;
        if (user.id && item.id) return item.id === user.id;
        return false;
      }) || null;
  }

  // Fetch informasi (berita)
  const { data: informasiData } = useAPI(endpoints.informasi.getAll);

  // Fetch master program & penyaluran program
  const { data: programMasterData } = useAPI(endpoints.program.getAll);
  const { data: penyaluranData } = useAPI(endpoints.penyaluran_program.getAll);

  const today = new Date();
  const formattedDate = today.toLocaleDateString('id-ID', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const displayName =
    isGuest
      ? 'Sahabat Dermawan'
      : currentDonatur?.nama || user?.name || user?.nama || 'Sahabat Dermawan';

  const highlightCards = [
    {
      label: 'Donasi Fleksibel',
      value: 'Zakat, Infak & Sedekah',
      description: 'Pilih jenis donasi sesuai niat dan tunaikan dalam beberapa langkah mudah.',
      pillLabel: 'Mudah Diakses',
      iconBg: 'bg-amber-100/80 text-amber-800 border-amber-200',
      svg: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      )
    },
    {
      label: 'Pembayaran Praktis',
      value: 'Transfer & QRIS Instant',
      description: 'Dukungan berbagai metode pembayaran dengan konfirmasi aman dan transparan.',
      pillLabel: 'Siap Digunakan',
      iconBg: 'bg-blue-100 text-blue-900 border-blue-200',
      svg: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      label: 'Pantauan Transparan',
      value: 'Status Real-time',
      description: 'Pantau riwayat transaksi, notifikasi, dan penyaluran program langsung.',
      pillLabel: 'Semua Donatur',
      iconBg: 'bg-amber-50 text-amber-700 border-amber-200',
      svg: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
  ];

  // Olah & agregasikan data penyaluran program agar persentase dihitung akurat terhadap target
  const programs = useMemo(() => {
    let penyaluranList = [];
    if (Array.isArray(penyaluranData)) {
      penyaluranList = penyaluranData;
    } else if (penyaluranData?.penyaluran_program && Array.isArray(penyaluranData.penyaluran_program)) {
      penyaluranList = penyaluranData.penyaluran_program;
    } else if (penyaluranData?.data && Array.isArray(penyaluranData.data)) {
      penyaluranList = penyaluranData.data;
    }

    let masterList = [];
    if (Array.isArray(programMasterData)) {
      masterList = programMasterData;
    } else if (programMasterData?.program && Array.isArray(programMasterData.program)) {
      masterList = programMasterData.program;
    } else if (programMasterData?.data && Array.isArray(programMasterData.data)) {
      masterList = programMasterData.data;
    }

    if (penyaluranList.length > 0) {
      const programMap = new Map();

      penyaluranList.forEach((item) => {
        const progId = String(item.id_program || item.program?.id_program || item.program?.id || item.nama_program || item.id_penyaluran);
        const nama = item.program?.nama_program || item.nama_program || `Penyaluran ${item.sumber_dana || ''}`;
        const kategori = item.program?.divisi?.nama_divisi || item.sumber_dana || item.kategori || 'Penyaluran';
        const nominal = Number(item.nominal_penyaluran || item.nominal || 0) || 0;

        if (!programMap.has(progId)) {
          const matchedMaster = masterList.find((p) => String(p.id_program || p.id) === progId);
          const targetDana = Number(matchedMaster?.target_dana || matchedMaster?.target || item.target_dana || item.target || 50000000);
          const customProgress = item.persentase || item.progress || item.progres;

          programMap.set(progId, {
            id: progId,
            nama,
            kategori,
            totalNominal: nominal,
            targetDana,
            customProgress: customProgress ? parseInt(customProgress) : null,
          });
        } else {
          const existing = programMap.get(progId);
          existing.totalNominal += nominal;
        }
      });

      const badgeColors = [
        'bg-blue-50 text-blue-900 border-blue-200',
        'bg-amber-50 text-amber-800 border-amber-200',
        'bg-blue-100/70 text-blue-800 border-blue-200',
      ];

      return Array.from(programMap.values()).slice(0, 3).map((prog, idx) => {
        let computedProgress = 0;
        if (prog.customProgress !== null) {
          computedProgress = Math.min(100, Math.max(5, prog.customProgress));
        } else if (prog.targetDana > 0 && prog.totalNominal > 0) {
          computedProgress = Math.min(95, Math.max(12, Math.round((prog.totalNominal / prog.targetDana) * 100)));
        } else {
          computedProgress = [45, 68, 39][idx % 3];
        }

        return {
          id: prog.id,
          nama: prog.nama,
          kategori: prog.kategori,
          progress: computedProgress,
          badgeColor: badgeColors[idx % badgeColors.length]
        };
      });
    }

    // Fallback jika API penyaluran program belum ada data
    return [
      {
        id: 1,
        nama: 'Beasiswa Santri Penghafal Qur’an',
        kategori: 'Pendidikan',
        progress: 72,
        badgeColor: 'bg-blue-50 text-blue-900 border-blue-200'
      },
      {
        id: 2,
        nama: 'Program Pemberdayaan UMKM Mustahik',
        kategori: 'Ekonomi',
        progress: 54,
        badgeColor: 'bg-amber-50 text-amber-800 border-amber-200'
      },
      {
        id: 3,
        nama: 'Layanan Kesehatan Dhuafa',
        kategori: 'Kesehatan',
        progress: 39,
        badgeColor: 'bg-blue-100/70 text-blue-800 border-blue-200'
      },
    ];
  }, [penyaluranData, programMasterData]);

  if (!isGuest && loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-amber-50">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-800"></div>
          <p className="text-blue-900 text-sm font-medium">
            Memuat data dashboard donatur...
          </p>
        </div>
      </div>
    );

  if (!isGuest && error)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-amber-50">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 max-w-md text-center">
          <p className="text-red-600 font-medium text-sm">{error}</p>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50/60 via-slate-50 to-amber-50/40 text-blue-900 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        
        {/* Top Header Card */}
        <div className="rounded-2xl p-5 md:p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1.5 flex-1">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-800 border border-blue-200/80">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
              MPZ DT • Dashboard Donatur
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-blue-900 tracking-tight">
              Assalamualaikum, {displayName}
            </h1>
            <p className="text-xs md:text-sm text-blue-700">
              Pantau zakat, infaq, dan sedekah Anda secara ringkas, aman, dan transparan.
            </p>
          </div>
          <div className="flex items-center gap-3 self-start md:self-center">
            <div className="bg-blue-50/70 border border-blue-200/60 rounded-xl px-4 py-2.5 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-900 text-amber-400 flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="text-left">
                <p className="text-[10px] uppercase font-bold text-blue-500 tracking-wider">Hari ini</p>
                <p className="text-xs font-semibold text-blue-900">{formattedDate}</p>
              </div>
            </div>
          </div>
        </div>

        {/* BARIS 1: Hero Banner (7 cols) + Program Penyaluran Unggulan (5 cols) sejajar sama tinggi */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          {/* Kolom Kiri: Hero Carousel */}
          <div className="lg:col-span-7">
            <section className="relative overflow-hidden rounded-2xl shadow-md h-64 sm:h-72 md:h-80 group">
              {/* Background Images Carousel */}
              {heroImages.map((image, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-950/90 via-blue-900/70 to-amber-900/30" />
                </div>
              ))}

              {/* Content Overlay */}
              <div className="relative z-10 h-full flex flex-col justify-between p-6 md:p-8 text-white">
                <div>
                  <span className="bg-amber-400/20 text-amber-300 border border-amber-400/30 backdrop-blur-md px-3 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase inline-block mb-3">
                    Selamat Datang Kembali
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold leading-tight drop-shadow-sm max-w-lg mb-2">
                    {heroImages[currentImageIndex].title}
                  </h2>
                  <p className="text-xs md:text-sm text-amber-100 max-w-md leading-relaxed drop-shadow-sm">
                    {heroImages[currentImageIndex].subtitle}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3 pt-4">
                  <button
                    onClick={() => navigate('/donasi/zakat')}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-400 hover:bg-amber-300 text-blue-950 text-xs md:text-sm font-bold shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5"
                  >
                    <span>Tunaikan Donasi</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                  <button
                    onClick={() => navigate('/edukasi-zakat')}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white text-xs md:text-sm font-semibold transition-all"
                  >
                    Pelajari Zakat
                  </button>
                </div>
              </div>

              {/* Carousel Indicators */}
              <div className="absolute bottom-5 right-6 flex items-center space-x-2 z-20">
                {heroImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentImageIndex
                        ? 'w-6 bg-amber-400'
                        : 'w-2 bg-white/50 hover:bg-white/80'
                    }`}
                  />
                ))}
              </div>
            </section>
          </div>

          {/* Kolom Kanan: Program Penyaluran Unggulan (Sejajar & Sama Tinggi dengan Hero Banner) */}
          <div className="lg:col-span-5">
            <section className="bg-white rounded-2xl border border-blue-100 p-5 shadow-md h-64 sm:h-72 md:h-80 flex flex-col justify-between">
              <div className="flex items-center justify-between pb-2 border-b border-blue-50">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-blue-100 text-blue-900 flex items-center justify-center">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <h3 className="text-xs md:text-sm font-bold text-blue-900">
                    Program Unggulan
                  </h3>
                </div>
              </div>

              <div className="space-y-2.5 flex-1 overflow-y-auto pt-2 pr-1">
                {programs.map((program) => (
                  <div
                    key={program.id}
                    className="rounded-xl border border-blue-100 bg-blue-50/40 p-3 hover:bg-white hover:border-amber-300 hover:shadow-sm transition-all duration-200 group"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="space-y-0.5 flex-1 min-w-0">
                        <span className={`inline-block text-[9px] font-bold px-2 py-0.5 rounded-md border ${program.badgeColor}`}>
                          {program.kategori}
                        </span>
                        <p className="text-xs font-bold text-blue-900 group-hover:text-amber-700 transition-colors truncate">
                          {program.nama}
                        </p>
                      </div>
                      <span className="text-[11px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-lg border border-amber-200 flex-shrink-0">
                        {program.progress}%
                      </span>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-blue-100 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-700 to-amber-500 rounded-full transition-all duration-500"
                        style={{ width: `${program.progress}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>

        {/* BARIS 2: Highlight Cards (7 cols) + Tips Berzakat (5 cols) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          {/* Kolom Kiri: Highlight Cards */}
          <div className="lg:col-span-7">
            <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 h-full">
              {highlightCards.map((item) => (
                <div
                  key={item.label}
                  className="bg-white rounded-2xl border border-blue-100 p-4 flex flex-col justify-between gap-3 shadow-sm hover:shadow-md hover:border-amber-300 transition-all duration-200"
                >
                  <div className="flex items-center justify-between">
                    <div className={`w-9 h-9 rounded-xl border flex items-center justify-center ${item.iconBg}`}>
                      {item.svg}
                    </div>
                    <span className="inline-flex items-center rounded-full bg-amber-100/70 text-amber-800 text-[10px] font-semibold px-2.5 py-0.5 border border-amber-200">
                      {item.pillLabel}
                    </span>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase text-blue-600 tracking-wider">{item.label}</p>
                    <p className="text-sm font-bold text-blue-900 mt-0.5">
                      {item.value}
                    </p>
                    <p className="text-[11px] text-slate-600 leading-relaxed mt-1">
                      {item.description}
                    </p>
                  </div>
                  <div className="flex gap-1 h-6 items-end pt-1">
                    <span className="w-1.5 rounded-full bg-blue-200 h-2.5" />
                    <span className="w-1.5 rounded-full bg-blue-300 h-3.5" />
                    <span className="w-1.5 rounded-full bg-blue-400 h-4.5" />
                    <span className="w-1.5 rounded-full bg-amber-400 h-5.5" />
                    <span className="w-1.5 rounded-full bg-amber-500 h-6" />
                  </div>
                </div>
              ))}
            </section>
          </div>

          {/* Kolom Kanan: Tips Berzakat */}
          <div className="lg:col-span-5">
            <section className="bg-white rounded-2xl border border-blue-100 p-5 shadow-sm space-y-3 h-full flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2 pb-1 border-b border-blue-50">
                  <div className="w-7 h-7 rounded-lg bg-amber-100 text-amber-800 flex items-center justify-center">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <h3 className="text-xs md:text-sm font-bold text-blue-900">
                    Tips Berzakat
                  </h3>
                </div>

                <div className="space-y-2">
                  <div className="flex items-start gap-2.5 p-2.5 rounded-xl bg-blue-50/60 hover:bg-blue-100/60 border border-blue-100 hover:border-blue-200 transition-colors cursor-pointer group">
                    <div className="flex-shrink-0 w-5 h-5 rounded-lg bg-blue-200 text-blue-800 flex items-center justify-center text-[10px] font-bold mt-0.5">
                      ✓
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-blue-900 group-hover:text-blue-950 transition-colors">Hitung Nisab Tepat</p>
                      <p className="text-[10px] text-blue-700/80 leading-snug">Pastikan total harta telah mencapai nisab zakat maalnya.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2.5 p-2.5 rounded-xl bg-amber-50/60 hover:bg-amber-100/60 border border-amber-100 hover:border-amber-200 transition-colors cursor-pointer group">
                    <div className="flex-shrink-0 w-5 h-5 rounded-lg bg-amber-200 text-amber-800 flex items-center justify-center text-[10px] font-bold mt-0.5">
                      ✓
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-blue-900 group-hover:text-amber-900 transition-colors">Pilih Program Sesuai Niat</p>
                      <p className="text-[10px] text-blue-700/80 leading-snug">Sesuaikan pilihan jenis zakat, infaq, atau sedekah Anda.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2.5 p-2.5 rounded-xl bg-blue-50/60 hover:bg-blue-100/60 border border-blue-100 hover:border-blue-200 transition-colors cursor-pointer group">
                    <div className="flex-shrink-0 w-5 h-5 rounded-lg bg-blue-200 text-blue-800 flex items-center justify-center text-[10px] font-bold mt-0.5">
                      ✓
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-blue-900 group-hover:text-blue-950 transition-colors">Pantau Penyaluran</p>
                      <p className="text-[10px] text-blue-700/80 leading-snug">Ikuti dampak kebaikan zakat Anda langsung via dashboard.</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* BARIS 3: Widget Simulasi Zakat & Target Donasi Bulanan */}
        <section className="bg-white rounded-2xl border border-blue-100 p-5 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-amber-100/80 text-amber-800 flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-bold text-blue-900">Target & Hitung Zakat</h3>
                <p className="text-[11px] text-blue-700">Kelola target kebaikan bulanan dan simulasi zakat Anda</p>
              </div>
            </div>
            <button
              onClick={() => setShowTargetModal(true)}
              className="px-3 py-1.5 rounded-xl bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-900 text-xs font-bold transition flex items-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>{monthlyTarget > 0 ? 'Edit Target' : 'Atur Target'}</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            {/* Box 1: Target Bulanan Status */}
            <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-3.5 flex flex-col justify-between space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase text-blue-600 tracking-wider">Target Bulanan</span>
                <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200">
                  {monthlyTarget > 0 ? 'Aktif' : 'Belum Set'}
                </span>
              </div>
              <div>
                <p className="text-base font-bold text-blue-900">
                  {monthlyTarget > 0
                    ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(monthlyTarget)
                    : 'Rp 0'}
                </p>
                <p className="text-[11px] text-slate-600 mt-0.5">
                  {monthlyTarget > 0
                    ? `Target harian ± ${new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(Math.ceil(monthlyTarget / 30))}`
                    : 'Tetapkan target donasi bulanan Anda'}
                </p>
              </div>
            </div>

            {/* Box 2: Quick Zakat Calculator Link */}
            <div className="bg-amber-50/50 border border-amber-100 rounded-xl p-3.5 flex flex-col justify-between space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase text-amber-800 tracking-wider">Simulasi Zakat</span>
                <span className="text-[10px] font-bold text-blue-900 bg-blue-50 px-2 py-0.5 rounded-md border border-blue-200">
                  Praktis
                </span>
              </div>
              <div>
                <p className="text-xs font-bold text-blue-900">Kalkulator Zakat Nisab</p>
                <p className="text-[11px] text-slate-600 mt-0.5">Hitung zakat penghasilan & emas dengan tepat</p>
              </div>
              <button
                onClick={() => navigate('/kalkulator-zakat')}
                className="w-full py-1.5 rounded-lg bg-amber-400 hover:bg-amber-300 text-blue-950 text-xs font-bold shadow-sm transition flex items-center justify-center gap-1 mt-1"
              >
                <span>Hitung Zakat Sekarang</span>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
            </div>
          </div>

          {/* Banner mini keunggulan */}
          <div className="rounded-xl bg-gradient-to-r from-blue-900 via-blue-800 to-blue-950 p-4 text-white flex flex-col sm:flex-row items-center justify-between gap-3 shadow-sm">
            <div className="space-y-0.5 text-center sm:text-left">
              <p className="text-xs font-bold text-amber-300">Zakat & Sedekah Lebih Berkah</p>
              <p className="text-[11px] text-blue-100">Disalurkan secara tepat sasaran untuk 8 Asnaf penerima manfaat.</p>
            </div>
            <div className="flex items-center gap-2 text-[10px] font-bold">
              <span className="px-2.5 py-1 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white">100% Transparan</span>
              <span className="px-2.5 py-1 rounded-lg bg-amber-400 text-blue-950">Amanah MPZ</span>
            </div>
          </div>
        </section>

        {!isGuest && !currentDonatur && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3 text-amber-900 text-xs">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p>
              Belum ada data donatur yang terhubung dengan akun ini. Silakan lengkapi profil untuk pengalaman yang lebih personal.
            </p>
          </div>
        )}

        {/* BARIS 4: Section Berita Zakat */}
        <section className="space-y-4 pt-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-blue-100 text-blue-900 flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-blue-900">
                  Berita & Edukasi Zakat
                </h3>
                <p className="text-xs text-blue-700">
                  Pelajari lebih lanjut tentang zakat dan dampak positifnya bagi penerima manfaat
                </p>
              </div>
            </div>
          </div>

          {(() => {
            let beritaList = [];
            
            // Parse berbagai format response API
            if (Array.isArray(informasiData)) {
              beritaList = informasiData;
            } else if (informasiData?.informasi_mahasiswa && Array.isArray(informasiData.informasi_mahasiswa)) {
              beritaList = informasiData.informasi_mahasiswa;
            } else if (informasiData?.informasi && Array.isArray(informasiData.informasi)) {
              beritaList = informasiData.informasi;
            } else if (informasiData?.data && Array.isArray(informasiData.data)) {
              beritaList = informasiData.data;
            }

            if (beritaList.length === 0) {
              return (
                <div className="text-center py-10 bg-white rounded-2xl border border-blue-100 shadow-sm">
                  <p className="text-blue-900 font-medium text-sm mb-1">Belum ada berita tersedia</p>
                  <p className="text-xs text-blue-600">Berita akan ditampilkan di sini setelah dipublikasikan</p>
                </div>
              );
            }

            return (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {beritaList.slice(0, 3).map((berita, idx) => (
                    <div
                      key={berita.id_informasi_mahasiswa || berita.id || idx}
                      className="bg-white rounded-2xl border border-blue-100 overflow-hidden shadow-sm hover:shadow-md hover:border-amber-400 transition-all duration-300 group cursor-pointer flex flex-col justify-between"
                      onClick={() => {
                        setSelectedBerita(berita);
                        setShowBeritaModal(true);
                      }}
                    >
                      <div>
                        <div className="h-44 w-full bg-slate-100 overflow-hidden relative">
                          {berita.foto ? (
                            <img
                              src={berita.foto}
                              alt={berita.judul}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-800 to-blue-950">
                              <svg className="w-12 h-12 text-amber-300/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                          <div className="absolute top-3 left-3">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-white/90 backdrop-blur-md text-blue-900 text-[11px] font-bold shadow-sm">
                              {berita.kategori || 'Berita'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="p-4 space-y-2">
                          <p className="text-[11px] font-medium text-blue-600">
                            {berita.tanggal ? new Date(berita.tanggal).toLocaleDateString('id-ID', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            }) : 'Terbaru'}
                          </p>
                          <h4 className="text-sm font-bold text-blue-950 group-hover:text-amber-600 transition-colors line-clamp-2 leading-snug">
                            {berita.judul}
                          </h4>
                          <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
                            {berita.deskripsi || berita.isi?.substring(0, 100)}
                          </p>
                        </div>
                      </div>

                      <div className="px-4 pb-4 pt-1">
                        <button 
                          className="inline-flex items-center gap-1 text-xs font-bold text-amber-600 hover:text-amber-700 transition"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedBerita(berita);
                            setShowBeritaModal(true);
                          }}
                        >
                          <span>Baca selengkapnya</span>
                          <svg className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}

                </div>

                {beritaList.length > 3 && (
                  <div className="mt-4 bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-blue-50 flex items-center justify-between">
                      <h4 className="text-sm font-bold text-blue-900">Berita Lainnya</h4>
                      <span className="text-xs text-blue-600">{beritaList.length - 3} item</span>
                    </div>

                    <div className="divide-y divide-blue-50">
                      {beritaList.slice(3).map((b, i) => (
                        <div
                          key={b.id_informasi_mahasiswa || b.id || i}
                          className="px-4 py-3 flex items-start gap-3 hover:bg-blue-50 transition-colors cursor-pointer"
                          onClick={() => {
                            setSelectedBerita(b);
                            setShowBeritaModal(true);
                          }}
                        >
                          <div className="flex-shrink-0 w-14 h-14 rounded-md bg-slate-100 overflow-hidden">
                            {b.foto ? (
                              <img src={b.foto} alt={b.judul} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-800 to-blue-950">
                                <svg className="w-8 h-8 text-amber-300/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-[11px] text-blue-600">
                                {b.tanggal ? new Date(b.tanggal).toLocaleDateString('id-ID', {
                                  day: 'numeric', month: 'short', year: 'numeric'
                                }) : 'Terbaru'}
                              </p>
                              <span className="text-[11px] font-semibold text-blue-900 bg-blue-50 px-2 py-0.5 rounded-md border border-blue-100">
                                {b.kategori || 'Berita'}
                              </span>
                            </div>

                            <h5 className="text-sm font-bold text-blue-900 truncate">{b.judul}</h5>
                            <p className="text-xs text-slate-600 mt-1 line-clamp-2">{b.deskripsi || b.isi?.substring(0, 120)}</p>
                          </div>

                          <div className="flex-shrink-0 pl-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedBerita(b);
                                setShowBeritaModal(true);
                              }}
                              className="text-xs font-bold text-amber-600 hover:text-amber-700"
                            >
                              Baca
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </section>

        {/* Modal Atur Target Donasi */}
        {showTargetModal && (
          <div className="fixed inset-0 bg-blue-950/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 border border-blue-100">
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-3 text-amber-800">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-blue-900">Atur Target Donasi Bulanan</h3>
                <p className="text-xs text-blue-700 mt-1">
                  Tetapkan target donasi bulanan Anda dan kami akan hitung estimasi target harian otomatis
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-blue-900 mb-1.5 uppercase tracking-wider">
                    Target Donasi Bulanan (Rp)
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-blue-700 font-bold text-sm">Rp</span>
                    <input
                      type="number"
                      value={targetInput}
                      onChange={(e) => setTargetInput(e.target.value)}
                      placeholder="Contoh: 1000000"
                      className="flex-1 px-4 py-2.5 rounded-xl border border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 text-right text-sm font-semibold text-blue-900"
                    />
                  </div>
                  <p className="text-[11px] text-blue-700 mt-2">
                    💡 Target harian otomatis: <span className="font-bold text-amber-600">{Math.ceil(parseInt(targetInput || 0) / 30).toLocaleString('id-ID', {
                      style: 'currency',
                      currency: 'IDR',
                      minimumFractionDigits: 0,
                    })}</span>
                  </p>
                </div>

                <div className="bg-blue-50/80 border border-blue-200 rounded-xl p-3.5 text-xs text-blue-900 space-y-1">
                  <p className="font-bold">Contoh perhitungan:</p>
                  <ul className="text-blue-800 space-y-0.5 list-disc list-inside text-[11px]">
                    <li>Target bulanan: Rp 1.000.000</li>
                    <li>Target harian: Rp 1.000.000 ÷ 30 = Rp 33.333</li>
                  </ul>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowTargetModal(false)}
                    className="flex-1 px-4 py-2.5 rounded-xl border border-slate-300 text-slate-700 text-xs font-bold hover:bg-slate-50 transition-colors"
                  >
                    Batal
                  </button>
                  <button
                    onClick={handleSaveTarget}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-blue-900 hover:bg-blue-800 text-white text-xs font-bold shadow-md transition-all"
                  >
                    Simpan Target
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Detail Berita */}
        {showBeritaModal && selectedBerita && (
          <div className="fixed inset-0 bg-blue-950/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full my-8 overflow-hidden border border-blue-100">
              {/* Foto Header */}
              <div className="h-64 bg-slate-100 overflow-hidden relative">
                {selectedBerita.foto ? (
                  <img
                    src={selectedBerita.foto}
                    alt={selectedBerita.judul}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-900 to-blue-950">
                    <svg className="w-16 h-16 text-amber-400/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-blue-950/80 via-transparent to-transparent"></div>
                
                {/* Close button */}
                <button
                  onClick={() => {
                    setShowBeritaModal(false);
                    setSelectedBerita(null);
                  }}
                  className="absolute top-4 right-4 w-9 h-9 bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white shadow-md transition-colors"
                >
                  <svg className="w-5 h-5 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <div className="absolute bottom-4 left-6 right-6 flex items-center justify-between text-white">
                  <span className="inline-flex items-center px-3 py-1 rounded-lg bg-amber-400 text-blue-950 text-xs font-bold shadow-md">
                    {selectedBerita.kategori || 'Berita'}
                  </span>
                  <span className="text-xs font-semibold text-amber-100 drop-shadow">
                    {selectedBerita.tanggal ? new Date(selectedBerita.tanggal).toLocaleDateString('id-ID', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    }) : 'Terbaru'}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 md:p-8 space-y-4">
                {/* Title */}
                <h2 className="text-xl md:text-2xl font-bold text-blue-900 leading-snug">
                  {selectedBerita.judul}
                </h2>

                {/* Full Description */}
                <div className="prose prose-sm max-w-none text-slate-600 leading-relaxed whitespace-pre-wrap pt-2">
                  {selectedBerita.deskripsi || selectedBerita.isi || 'Konten tidak tersedia'}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-6 border-t border-blue-100">
                  <button
                    onClick={() => {
                      setShowBeritaModal(false);
                      setSelectedBerita(null);
                    }}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-slate-100 text-slate-700 text-xs font-bold hover:bg-slate-200 transition-colors"
                  >
                    Tutup
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Home;
